import { AnchorHTMLAttributes, DetailedHTMLProps, MutableRefObject, ReactNode, forwardRef, useState } from 'react';
import { Link, LinkProps, NavLink, NavLinkProps } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { Div } from './Generic';
import { Size, UIProps } from '../../theme/mixins';
import { withScrollRestoration } from './helpers';
import SC from './SC';
import useDebouncedEffect from '../useDebouncedEffect';

export interface ButtonProps extends UIProps {
  brand?: string;
  size?: Size;
  outline?: boolean;
  blank?: boolean;
  active?: boolean;
  disabled?: boolean;
  block?: boolean;
  loading?: boolean;
  borderRadius?: Size;
}

const useButton = ({
  brand, size, outline, blank, active, disabled, loading, borderRadius, ...props
}: ButtonProps, theme: any) => {
  const color = theme.getColor(brand || 'gray.200');
  const backgroundColor = brand ? color : theme.transparentize(theme.colors.gray[500], 0.85);
  const hoverColor = brand
    ? theme.mix(theme.lightenColor(color, 4), 'white', 0.15)
    : theme.transparentize(theme.colors.gray[500], 0.9);
  const activeColor = brand
    ? theme.transparentize(theme.mix(theme.darkenColor(color, 20), color, 0.5), outline ? 0.5 : 0)
    : theme.transparentize(theme.colors.gray[500], 0.75);

  return css`
    display: inline-block;
    background: ${backgroundColor};
    border: none;
    color: ${theme.getTitleColor(color)};
    text-align: center;
    cursor: pointer;
    font-weight: 500;
    transition: color 0.15s ease-in-out, border-color 0.15s ease-in-out, background-color 0.15s ease-in-out, drop-shadow 0.15s ease-in-out;
    margin: 0;
    line-height: 20px;
    white-space: nowrap;

    ${!outline && !blank && css`
      box-shadow: inset 0 -1px 1px -1px rgba(0, 0, 0, .35);
    `}

    ${props.block && css`
      width: 100%;
    `}

    &:hover {
      background: ${hoverColor};
      border-color: ${hoverColor};
    }

    ${outline && css`
      background: transparent;
      border: 1px solid ${color};
      color: ${theme.getColor((typeof outline === 'string' ? outline : brand) || 'gray.900')};
      line-height: 18px;

      &:hover {
        background: ${hoverColor};
        border-color: ${color} !important;
      }
    `}

    ${blank && css`
      background: transparent;

      &:hover {
        background: ${hoverColor};
        border-color: transparent;
      }
    `}

    &:active, &.active {
      background: ${activeColor} !important;
    }

    ${active && css`
      background: ${activeColor} !important;
    `};

    &:focus-visible {
      outline-offset: 2px;
    }

    &:disabled {
      opacity: 0.5;
    }

    ${disabled && css`
      opacity: 0.5;
      cursor: default;
      pointer-events: none;
    `}

    ${loading && css`
      animation: pulse 1.5s infinite;

      @keyframes pulse {
        30% {
          box-shadow: 0 0 0 0 ${theme.transparentize(theme.darkenColor(color, 20), 0.5)};
        }
        100% {
          box-shadow: 0 0 0 7px rgba(0, 0, 0, 0);
        }
      }
    `}

    ${!outline && theme.useSizes(size, 'padding', ['5px 3px', '5px 8px', '9px 12px', '13px 18px', '13px 18px', '13px 18px'])}
    ${outline && theme.useSizes(size, 'padding', ['4px 2px', '4px 7px', '8px 11px', '12px 17px', '12px 17px', '12px 17px'])}
    ${theme.useSizes(size, 'height', ['30px', '30px', '38px', '46px', '46px'])}
    ${theme.useSizes(size, 'font-size', [`${12 / 14}em`, `${13 / 14}em`])}
    ${theme.useSizes(borderRadius || size, 'border-radius', [`${theme.borderRadiuses.md}px`, `${theme.borderRadiuses.md}px`, `${theme.borderRadiuses.md}px`, `${theme.borderRadiuses.lg}px`, `${theme.borderRadiuses.xl}px`])}
    ${props && theme.useMixins(props)};
  `;
};

export const Button = styled.button.attrs((props) => ({ type: 'button', ...props }))<SC<ButtonProps>>`
  ${({ sc: props, disabled, theme }) => useButton({ ...props, disabled }, theme)}
`;

export const LinkButton = styled(withScrollRestoration(Link, 'LinkButton'))<SC<ButtonProps> & LinkProps>`
  ${({ sc: props, theme }) => useButton({ ...props }, theme)}
`;

export const NavButton = styled(withScrollRestoration(NavLink, 'NavButton'))<SC<ButtonProps> & NavLinkProps>`
  ${({ sc: props, theme }) => useButton({ ...props }, theme)}
`;

// eslint-disable-next-line jsx-a11y/anchor-has-content
export const AButton = styled(forwardRef(({ sc, ...props }: DetailedHTMLProps<AnchorHTMLAttributes<HTMLAnchorElement>, HTMLAnchorElement> & SC<ButtonProps>, ref: MutableRefObject<HTMLAnchorElement>) => <a {...props} ref={ref} />))<SC<ButtonProps>>`
  ${({ sc: props, theme }) => useButton({ ...props }, theme)}
`;

interface TabButtonProps extends UIProps {
  active?: boolean;
}

export const TabButton = styled.button<SC<TabButtonProps>>`
  ${({ sc = {}, theme }) => css`
    background: transparent;
    border: none;
    padding: 0;
    border-bottom: 3px solid transparent;
    cursor: pointer;
    border-radius: 0;
    padding: 13px 0 10px 0;
    height: 48px;
    color: ${theme.colors.gray[600]};
    font-weight: 500;

    &:hover {
      color: ${theme.colors.gray[900]};
      border-color: ${theme.colors.gray[250]};
    }

    &:focus {
      background: transparent;
    }

    ${sc.active && css`
      color: ${theme.colors.gray[900]} !important;
      border-color: ${theme.colors.secondary[500]} !important;
    `}

    ${theme.useMixins(sc)};
  `}
`;

interface ConfirmButtonProps extends SC<ButtonProps> {
  onConfirm: () => any;
  render: (start: () => any, disabled: boolean) => ReactNode;
  loading?: boolean;
}

export const ConfirmButton = ({ onConfirm, render, loading = false, sc }: ConfirmButtonProps) => {
  const { t } = useTranslation();

  const [deleting, setDeleting] = useState(false);
  const [confirming, setConfirming] = useState(false);

  const start = () => {
    setDeleting(true);
  };

  useDebouncedEffect(() => {
    if (deleting) {
      setConfirming(true);
    }
  }, 100, [deleting]);

  useDebouncedEffect(() => {
    if (confirming) {
      setDeleting(false);
      setConfirming(false);
    }
  }, 10000, [confirming]);

  if (!confirming) {
    return <>{render(start, deleting)}</>;
  }

  return (
    <Button
      onClick={() => onConfirm()}
      sc={{ loading, ...sc }}
      disabled={loading}
    >
      {t('confirm')}
    </Button>
  );
};

const ToggleButtonContainer = styled.div<SC<ButtonProps>>`
  ${({ sc, theme }) => css`
    display: ${sc && sc.block ? 'block' : 'inline-block'};
    position: relative;

    button {
      display: block;
      text-align: left;
      padding: 10px 24px 10px 10px;
    }

    ::after {
      content: '';
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
      right: 10px;
      width: 0;
      height: 0;
      margin-left: 8px;
      border-left: 4px solid transparent;
      border-right: 4px solid transparent;
      border-top: 4px solid ${theme.getTextColor(theme.getColor((sc && sc.brand) || 'gray.200'))};
      opacity: 0.5;
      display: inline-block;
      margin-bottom: 2px;
    }
  `}
`;

export const ToggleButton = ({ sc, ...props }: any) => (
  <ToggleButtonContainer sc={{ block: sc && sc.block }}>
    <Button type="button" sc={sc} {...props} />
  </ToggleButtonContainer>
);

export const Pill = styled(Div)`
  display: flex;

  button:not(:first-child), a:not(:first-child) {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  button:not(:last-child), a:not(:last-child) {
    border-right-width: 0;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }
`;
